"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/Card";
import { 
  Users, 
  MessageSquare, 
  ShieldCheck, 
  CheckCircle2, 
  Sparkles, 
  Clock, 
  ChevronRight,
  UserCheck,
  Building
} from "lucide-react";

interface PipelineStep {
  id: string;
  title: string;
  icon: React.ReactNode;
  kicker: string;
  description: string;
}

export const PipelineSimulator: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState<number>(0);

  const steps: PipelineStep[] = [
    {
      id: "sourced",
      title: "1. AI Sourced",
      icon: <Users className="h-4 w-4" />,
      kicker: "Sourcing Matching Engine",
      description: "AI scans and matches candidates from premium engineering pools instantly.",
    },
    {
      id: "vetted",
      title: "2. Vet & Screen",
      icon: <MessageSquare className="h-4 w-4" />,
      kicker: "Vetting & Automated Interview",
      description: "AI schedules and conducts preliminary screening interviews.",
    },
    {
      id: "verified",
      title: "3. Compliance",
      icon: <ShieldCheck className="h-4 w-4" />,
      kicker: "Verification & Documentation",
      description: "Automated verification of past records, ESIC, EPFO, and certifications.",
    },
    {
      id: "placed",
      title: "4. Placed & Onboard",
      icon: <CheckCircle2 className="h-4 w-4" />,
      kicker: "Onboarding and Placement",
      description: "Seamless deployment of the candidate to your project teams.",
    },
  ];

  return (
    <Card className="w-full relative overflow-hidden" glowColor="rgba(37, 99, 235, 0.12)">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8">
        
        {/* Left Side: Pipeline Steps Stepper */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-widest text-primary bg-primary/5 dark:bg-primary/10 px-2.5 py-1 rounded-md">
              Interactive Demo
            </span>
            <h3 className="font-heading font-extrabold text-xl text-foreground mt-3">
              Candidate Lifecycle
            </h3>
            <p className="text-xs text-text-muted mt-1">
              Click a step below to simulate how TalentFlow AI automates hiring workflows.
            </p>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            {steps.map((step, idx) => {
              const isActive = activeStep === idx;
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(idx)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all cursor-pointer ${
                    isActive
                      ? "bg-primary border-primary text-white shadow-md shadow-primary/15"
                      : "bg-white dark:bg-slate-900 border-border-muted hover:border-primary/50 text-foreground"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isActive ? "bg-white/20 text-white" : "bg-primary/5 text-primary"}`}>
                      {step.icon}
                    </div>
                    <span className="text-xs font-bold">{step.title}</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform ${isActive ? "rotate-90 text-white" : "text-text-muted"}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Side: Simulation Panel (Visual Sandbox) */}
        <div className="lg:col-span-8 bg-slate-50 dark:bg-slate-950 rounded-xl border border-border-muted/50 p-6 flex flex-col justify-between min-h-[360px] relative overflow-hidden">
          
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
              className="flex-grow flex flex-col justify-between"
            >
              
              {/* Sandbox Header */}
              <div className="flex justify-between items-start border-b border-border-muted/50 pb-4 mb-6">
                <div>
                  <span className="text-[9px] uppercase font-bold tracking-widest text-primary dark:text-accent">
                    {steps[activeStep].kicker}
                  </span>
                  <p className="text-xs text-text-muted mt-1">
                    {steps[activeStep].description}
                  </p>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  Live Simulator
                </div>
              </div>

              {/* Sandbox Body Content (Changes based on step) */}
              <div className="flex-grow flex flex-col justify-center">
                {activeStep === 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-foreground mb-1">Top Matching Candidates Sourced:</p>
                    
                    {/* Candidate Row 1 */}
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-primary text-white flex items-center justify-center font-bold text-xs">
                          AK
                        </div>
                        <div>
                          <p className="text-xs font-bold text-foreground">Ankit Kumar</p>
                          <p className="text-[10px] text-text-muted">Lead React Native Engineer · 6 yrs</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block text-[10px] font-bold bg-success/10 text-success px-2 py-0.5 rounded">
                          98% Match
                        </span>
                      </div>
                    </div>

                    {/* Candidate Row 2 */}
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-accent text-primary flex items-center justify-center font-bold text-xs">
                          SR
                        </div>
                        <div>
                          <p className="text-xs font-bold text-foreground">Saniya Roy</p>
                          <p className="text-[10px] text-text-muted">Senior DevOps Engineer (AWS/Docker) · 8 yrs</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block text-[10px] font-bold bg-success/10 text-success px-2 py-0.5 rounded">
                          94% Match
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeStep === 1 && (
                  <div className="space-y-4">
                    <p className="text-xs font-bold text-foreground mb-1">AI Screening Log:</p>
                    
                    <div className="p-3.5 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30 space-y-3">
                      <div className="flex gap-2">
                        <span className="text-[10px] font-bold text-primary shrink-0">[AI Evaluator]:</span>
                        <p className="text-xs text-foreground/80 leading-relaxed">
                          "Tell me about your experience scaling API performance. What tools do you use?"
                        </p>
                      </div>
                      <div className="flex gap-2 border-t border-border-muted/30 pt-2.5">
                        <span className="text-[10px] font-bold text-accent shrink-0">[Candidate]:</span>
                        <p className="text-xs text-foreground/90 italic leading-relaxed">
                          "I used Redis caching and load balancers to reduce database latency by 45% for our logistics app..."
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 items-center text-[10px] font-bold text-success">
                      <Sparkles className="h-4 w-4" />
                      <span>AI Recommendation: Highly suitable. Proceed to compliance check.</span>
                    </div>
                  </div>
                )}

                {activeStep === 2 && (
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-foreground mb-1">Compliance Checklist:</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2.5 p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                        <ShieldCheck className="h-5 w-5 text-success shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-foreground">PF / EPFO Sync</p>
                          <p className="text-[9px] text-text-muted">Verified background history</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                        <ShieldCheck className="h-5 w-5 text-success shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-foreground">UAN Verification</p>
                          <p className="text-[9px] text-text-muted">Employer matching confirmed</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                        <ShieldCheck className="h-5 w-5 text-success shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-foreground">ESIC Records</p>
                          <p className="text-[9px] text-text-muted">Health insurance accounts active</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 p-3 bg-white dark:bg-slate-900 rounded-lg border border-border-muted/30">
                        <ShieldCheck className="h-5 w-5 text-success shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-foreground">Identity Check</p>
                          <p className="text-[9px] text-text-muted">PAN/Aadhaar compliance verified</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeStep === 3 && (
                  <div className="space-y-4">
                    <p className="text-xs font-bold text-foreground mb-1">Onboarding Checklist Complete:</p>

                    <div className="p-4 bg-emerald-500/5 dark:bg-emerald-500/10 border border-success/30 rounded-xl flex items-center gap-4">
                      <div className="p-3 bg-success/20 rounded-full text-success">
                        <UserCheck className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-sm font-extrabold text-foreground">Candidate Onboarded Successfully</p>
                        <p className="text-xs text-text-muted mt-0.5">
                          Equipment shipped, NDA signed, Slack & GitHub permissions configured.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-xs font-bold text-foreground">
                      <div className="flex items-center gap-1.5">
                        <Building className="h-4 w-4 text-primary" />
                        <span>Sankalpa Logistics Ltd</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>Placed in 2 days</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sandbox Footer */}
              <div className="border-t border-border-muted/50 pt-4 mt-6 flex justify-between items-center text-[10px] text-text-muted">
                <span>Automated via API workflows</span>
                <span>TalentFlow AI v2.4</span>
              </div>

            </motion.div>
          </AnimatePresence>

        </div>

      </div>
    </Card>
  );
};
